import React from 'react';

import { Box, Container, Typography, Grid, Paper, Card, CardContent, Chip, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Stack, Divider } from '@mui/material';

import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import ArchitectureIcon from '@mui/icons-material/Architecture';
import ScienceIcon from '@mui/icons-material/Science';
import FactoryIcon from '@mui/icons-material/Factory';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EngineeringIcon from '@mui/icons-material/Engineering';
import SpeedIcon from '@mui/icons-material/Speed';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import { Link } from 'react-router-dom';

const Services = () => {
  const servicesList = [
    {
      title: 'CNC VMC Machining',
      desc: 'Our state-of-the-art Vertical Machining Centers (VMC) are capable of handling complex geometries with tight tolerances. We specialize in high-speed machining for aluminum and tough-material machining for steel and titanium. Our 3-axis and 4-axis capabilities allow us to produce intricate parts with minimal setups, ensuring higher accuracy and faster turnaround times.',
      details: ['3-Axis & 4-Axis Milling', 'High-Speed Machining (HSM)', 'Complex 3D Contouring', 'Rigid Tapping & Thread Milling', 'Aerospace Grade Tolerances'],
      icon: <PrecisionManufacturingIcon fontSize="large" color="primary" />
    },
    {
      title: 'Precision CNC Turning',
      desc: 'We offer advanced CNC turning services for cylindrical components. From simple shafts to complex turned parts with live tooling requirements, our turning centers deliver exceptional surface finishes and dimensional consistency. We handle diameters ranging from 10mm to 300mm with precision up to +/- 5 microns.',
      details: ['Live Tooling Capabilities', 'Hard Turning', 'Knurling, Threading & Grooving', 'Shafts, Pins, Bushings & Flanges', 'High-Volume Auto-Bar Feeding'],
      icon: <SettingsSuggestIcon fontSize="large" color="primary" />
    },
    {
      title: 'Engineering & Design Support',
      desc: 'Manufacturing is more than just cutting metal. Our engineering team uses advanced CAD/CAM software (Mastercam, SolidWorks) to optimize your designs for manufacturability (DFM). We help reduce costs and improve part quality by suggesting design modifications before production begins.',
      details: ['DFM (Design for Manufacturing) Analysis', 'CAD/CAM Programming (Mastercam)', 'Fixture Design & fabrication', 'Process Optimization', 'Reverse Engineering'],
      icon: <DesignServicesIcon fontSize="large" color="primary" />
    },
    {
      title: 'Rapid Prototyping',
      desc: 'Speed to market is critical. Our dedicated prototyping cell bridges the gap between design and mass production. We produce functional prototypes in production-grade materials, allowing you to validate your designs quickly. We offer fast-track services to deliver parts in as little as 48 hours.',
      details: ['Functional Metal Prototypes', 'Low-Volume Production Runs', 'Design Validation Models', 'Quick-Turn Machining', 'Iterative Product Development'],
      icon: <ArchitectureIcon fontSize="large" color="primary" />
    },
    {
      title: 'Batch & Mass Production',
      desc: 'Whether you need 50 parts or 50,000, we have the capacity to scale. Our facility works 24/7 with automated setups to ensure on-time delivery for high-volume orders. We implement lean manufacturing principles to minimize waste and maximize efficiency, passing the cost savings on to you.',
      details: ['Scalable Production Lines', 'Automated Workholding', 'Scheduled Deliveries (JIT)', 'Volume Pricing', 'Consistent Quality Control'],
      icon: <FactoryIcon fontSize="large" color="primary" />
    },
    {
      title: 'Surface Finishing & Treatment',
      desc: 'A part isn\'t complete until it meets all surface requirements. We offer comprehensive in-house finishing and have a network of certified partners for specialized treatments. From bead blasting to anodizing, we ensure your parts look as good as they function.',
      details: ['Anodizing (Clear, Black, Hard)', 'Zinc & Nickel Plating', 'Powder Coating & Painting', 'Bead Blasting & Tumbling', 'Heat Treatment & Stress Relieving'],
      icon: <ScienceIcon fontSize="large" color="primary" />
    }
  ];

  const materials = [
    { category: 'Steel Alloys', grades: 'Mild Steel (1018, 1045), Alloy Steel (4140, 4340), Tool Steel (D2, A2, O1)' },
    { category: 'Stainless Steel', grades: '300 Series (303, 304, 316, 316L), 400 Series (410, 416, 420, 440C), 17-4 PH' },
    { category: 'Aluminum', grades: '6061-T6 (Standard), 7075-T6 (High Strength), 2024 (Aerospace), 5052, MIC6' },
    { category: 'Yellow Metals', grades: 'Brass (C360), Bronze (C932, C954), Copper (C110, C101)' },
    { category: 'Titanium & Exotics', grades: 'Titanium Grade 2 & Grade 5 (6Al-4V), Inconel 625/718, Hastelloy' },
    { category: 'Plastics', grades: 'Delrin (Acetal), Nylon 6/6, PEEK, PTFE (Teflon), ABS, Polycarbonate' }
  ];

  const machineList = [
    { type: 'VMC 3-Axis', make: 'Haas VF-2', spec: '762 x 406 x 508 mm', qty: 4, desc: 'High-speed spindles for rapid material removal.' },
    { type: 'VMC 4-Axis', make: 'Mazak VCN-530', spec: '1050 x 530 x 510 mm', qty: 2, desc: 'Rotary table for multi-sided machining.' },
    { type: 'Turning Center', make: 'Doosan Puma GT', spec: 'Max Dia 300mm, Length 500mm', qty: 3, desc: 'Rigid box-ways for heavy cutting.' },
    { type: 'Surface Grinder', make: 'Kent KGS', spec: 'Automatic Surface Grinding', qty: 1, desc: 'Precision flatness and surface finish.' },
    { type: 'CMM', make: 'Mitutoyo Crysta', spec: '500 x 700 x 400 mm', qty: 1, desc: 'Coordinate Measuring Machine for verification.' },
    { type: 'Band Saw', make: 'Automatic Cut-off', spec: 'Up to 300mm Dia', qty: 2, desc: 'High-volume raw material preparation.' },
    { type: 'Height Gauge', make: 'Trimos V4', spec: '0-600mm', qty: 2, desc: 'Digital manufacturing height measurement.' },
    { type: 'Roughness Tester', make: 'Mitutoyo', spec: 'Ra, Rz parameters', qty: 2, desc: 'Surface finish verification.' }
  ];

  const processFlow = [
    { step: '01', title: 'Consultation & Review', desc: 'Understanding your requirements, reviewing part drawings, and conducting technical feasibility studies.' },
    { step: '02', title: 'Process Engineering', desc: 'Creating optimal machining strategies, selecting tools, designing fixtures, and generating CAM programs.' },
    { step: '03', title: 'Verification', desc: 'Running simulation software to detect errors and producing a "First Off" sample for quality verification.' },
    { step: '04', title: 'Production', desc: 'High-efficiency machining with in-process gauging and robust quality monitoring systems.' },
    { step: '05', title: 'Quality Assurance', desc: 'Final inspection using CMM and calibrated gauges. Generation of inspection reports and COCs.' },
    { step: '06', title: 'Finishing & Assembly', desc: 'Post-processing treatments, sub-assembly (if required), and preservation packaging.' },
    { step: '07', title: 'Logistics', desc: 'Timely dispatch via our logistics partners with tracking and documentation.' }
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box sx={{
        bgcolor: '#263238',
        color: 'white',
        py: { xs: 8, md: 16 },
        textAlign: 'center',
        backgroundImage: 'linear-gradient(rgba(0,0,0,0.8), rgba(0,0,0,0.7)), url(/images/cnc_hero.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }}>
        <Container maxWidth="lg">
          <Typography variant="h1" component="h1" fontWeight="900" sx={{ fontSize: { xs: '2.5rem', md: '4rem' }, mb: 2 }}>
            Engineering Excellence
          </Typography>
          <Typography variant="h4" sx={{ opacity: 0.9, fontWeight: 300, maxWidth: '800px', mx: 'auto', mb: 4 }}>
            Comprehensive manufacturing capabilities from rapid prototyping to high-volume production.
          </Typography>
          <Button component={Link} to="/contact" variant="contained" color="primary" size="large" sx={{ py: 1.5, px: 5, fontSize: '1.2rem', fontWeight: 'bold' }}>
            Get a Quote
          </Button>
        </Container>
      </Box>

      {/* Core Services Section */}
      <Container maxWidth="lg" sx={{ py: 12 }}>
        <Typography variant="overline" color="primary" fontWeight="bold" sx={{ display: 'block', mb: 1, letterSpacing: 2 }}>
          CORE COMPETENCIES
        </Typography>
        <Typography variant="h3" gutterBottom fontWeight="900" sx={{ mb: 2, color: '#1a237e' }}>
          Precision Manufacturing Services
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 8, maxWidth: '800px', fontSize: '1.1rem' }}>
          At AYDA INDUSTRIES, we leverage advanced technology and decades of expertise to deliver superior components. Our diverse capabilities allow us to serve as a one-stop shop for all your precision manufacturing needs.
        </Typography>

        <Grid container spacing={4}>
          {servicesList.map((service, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Paper
                elevation={2}
                sx={{
                  p: 4,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': { transform: 'translateY(-5px)', boxShadow: 10, borderColor: 'primary.main' },
                  border: '1px solid transparent',
                  borderLeft: `4px solid #0d47a1`
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Box sx={{ p: 1.5, bgcolor: 'rgba(13, 71, 161, 0.1)', borderRadius: 2, mr: 2, color: 'primary.main' }}>
                    {service.icon}
                  </Box>
                  <Typography variant="h5" fontWeight="bold" color="#263238">{service.title}</Typography>
                </Box>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3, flexGrow: 1, lineHeight: 1.7 }}>
                  {service.desc}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={1}>
                  {service.details.map((detail, i) => (
                    <Grid item xs={12} sm={6} key={i}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main', mr: 1 }} />
                        <Typography variant="caption" fontWeight="bold" color="text.primary">{detail}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>


      {/* Machine Capacity Table (Detailed) */}
      <Box sx={{ bgcolor: '#f5f7fa', py: 12 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" align="center" fontWeight="900" sx={{ mb: 2, color: '#263238' }}>Machine Infrastructure</Typography>
          <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 8, maxWidth: '700px', mx: 'auto' }}>
            We continuously invest in the latest machinery to maintain our competitive edge. Our strict maintenance schedules ensure high uptime and consistent accuracy across all equipment.
          </Typography>

          <Paper elevation={5} sx={{ borderRadius: 3, overflow: 'hidden', width: '100%' }}>
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table stickyHeader aria-label="machine capacity table" sx={{ minWidth: { xs: 800, md: '100%' } }}>
                <TableHead sx={{ bgcolor: '#0d47a1' }}>
                  <TableRow>
                    <TableCell sx={{ bgcolor: '#0d47a1', color: 'white', fontWeight: 'bold', fontSize: '1rem', py: 2 }}>Machine Type</TableCell>
                    <TableCell sx={{ bgcolor: '#0d47a1', color: 'white', fontWeight: 'bold', fontSize: '1rem', py: 2 }}>Make / Model</TableCell>
                    <TableCell sx={{ bgcolor: '#0d47a1', color: 'white', fontWeight: 'bold', fontSize: '1rem', py: 2 }}>Specifications</TableCell>
                    <TableCell sx={{ bgcolor: '#0d47a1', color: 'white', fontWeight: 'bold', fontSize: '1rem', py: 2 }}>Description</TableCell>
                    <TableCell align="center" sx={{ bgcolor: '#0d47a1', color: 'white', fontWeight: 'bold', fontSize: '1rem', py: 2 }}>Qty</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {machineList.map((row, index) => (
                    <TableRow key={index} sx={{ '&:nth-of-type(odd)': { bgcolor: 'white' }, '&:nth-of-type(even)': { bgcolor: '#e3f2fd' }, transition: '0.2s', '&:hover': { bgcolor: '#bbdefb' } }}>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'bold', color: '#1565c0' }}>{row.type}</TableCell>
                      <TableCell>{row.make}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontWeight: 500 }}>{row.spec}</TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>{row.desc}</TableCell>
                      <TableCell align="center"><Chip label={row.qty} size="small" color="primary" sx={{ fontWeight: 'bold' }} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Container>
      </Box>

      {/* Material Expertise */}
      <Box sx={{ py: 12 }}>
        <Container maxWidth="lg">
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={5}>
              <Typography variant="overline" color="warning.main" fontWeight="bold" letterSpacing={1.5}>MATERIALS</Typography>
              <Typography variant="h3" fontWeight="900" sx={{ mb: 3 }}>
                Material Versatility
              </Typography>
              <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', color: 'text.secondary', lineHeight: 1.8 }}>
                Different industries require different material properties. Whether it's the high strength-to-weight ratio of titanium for aerospace or the electrical conductivity of copper for electronics, we have the tooling and expertise to machine it all.
              </Typography>
              <img src="/images/parts.png" alt="Machined Parts" style={{ width: '100%', borderRadius: '12px', boxShadow: '0 10px 30px rgba(0,0,0,0.15)' }} />
            </Grid>
            <Grid item xs={12} md={7}>
              <Grid container spacing={3}>
                {materials.map((mat, index) => (
                  <Grid item xs={12} key={index}>
                    <Card elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
                      <CardContent>
                        <Typography variant="h6" fontWeight="bold" color="primary.main" gutterBottom>
                          {mat.category}
                        </Typography>
                        <Divider sx={{ mb: 1.5 }} />
                        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                          {mat.grades}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Engineering Process Flow */}
      <Box sx={{ bgcolor: '#263238', color: 'white', py: 12 }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={10}>
            <Typography variant="overline" color="secondary" fontWeight="bold">OUR PROCESS</Typography>
            <Typography variant="h3" fontWeight="900">From Order to Delivery</Typography>
            <Typography variant="body1" sx={{ maxWidth: '600px', mx: 'auto', mt: 2, opacity: 0.7 }}>
              A systematic approach ensures that quality is built into every step of the manufacturing journey, not just inspected at the end.
            </Typography>
          </Box>
          <Grid container spacing={4}>
            {processFlow.map((step, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                <Box
                  sx={{
                    p: 3,
                    height: '100%',
                    bgcolor: 'rgba(255,255,255,0.05)',
                    borderRadius: 3,
                    border: '1px solid rgba(255,255,255,0.1)',
                    transition: '0.3s',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.1)', transform: 'translateY(-5px)' }
                  }}
                >
                  <Typography variant="h2" fontWeight="900" sx={{ opacity: 0.1, mb: -4, ml: -1 }}>{step.step}</Typography>
                  <Box position="relative" zIndex={1}>
                    <Typography variant="h6" fontWeight="bold" color="secondary.main" gutterBottom>{step.title}</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8, lineHeight: 1.6 }}>{step.desc}</Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* FAQ / Capabilities Quick List */}
      <Box sx={{ py: 12, bgcolor: '#fff' }}>
        <Container maxWidth="lg">
          <Typography variant="h4" fontWeight="bold" textAlign="center" mb={6}>Why Choose AYDA INDIA?</Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <EngineeringIcon fontSize="large" color="primary" />
                <Box>
                  <Typography variant="h6" fontWeight="bold">Technical Expertise</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Our engineers have decades of experience in tool path optimization and fixture design, solving difficult machining challenges.
                  </Typography>
                </Box>
              </Stack>
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <SpeedIcon fontSize="large" color="primary" />
                <Box>
                  <Typography variant="h6" fontWeight="bold">Rapid Turnaround</Typography>
                  <Typography variant="body2" color="text.secondary">
                    With optimized workflows and 24/7 operations, we offer some of the fastest lead times in the industry for both prototypes and production.
                  </Typography>
                </Box>
              </Stack>
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <PrecisionManufacturingIcon fontSize="large" color="primary" />
                <Box>
                  <Typography variant="h6" fontWeight="bold">Cost Efficiency</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Lean manufacturing practices and smart tooling choices allow us to keep costs competitive without compromising on quality.
                  </Typography>
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Final CTA */}
      <Box sx={{
        bgcolor: '#0d47a1',
        py: 12,
        backgroundImage: 'linear-gradient(45deg, #0d47a1 30%, #1565c0 90%)',
        color: 'white',
        textAlign: 'center'
      }}>
        <Container maxWidth="md">
          <Typography variant="h3" fontWeight="900" gutterBottom>
            Ready to Start Your Project?
          </Typography>
          <Typography variant="h6" sx={{ mb: 5, opacity: 0.9 }}>
            Contact our engineering team today for a design review and a competitive quote.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button component={Link} to="/contact" variant="contained" color="secondary" size="large" sx={{ py: 1.5, px: 6, fontSize: '1.1rem', fontWeight: 'bold' }}>
              Request a Quote
            </Button>
            <Button component={Link} to="/quality" variant="outlined" color="inherit" size="large" sx={{ py: 1.5, px: 6, fontSize: '1.1rem', fontWeight: 'bold' }}>
              View Quality Policy
            </Button>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};

export default Services;
